import {
  Component,
  AfterViewInit,
  ViewChild,
  TemplateRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { ApplicationRating } from '@app/core/types/feedback/feedback.types';
import { awaitSuccessOrCancel } from '@app/shared/utils/pipe.functions';
import { Observable } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';

enum RateAppTemplate {
  Like,
  Rate,
  Feedback,
}

interface RateAppTemplateData {
  id: RateAppTemplate;
  template: TemplateRef<any>;
}

@Component({
    selector: 'c-rate-application-modal',
    templateUrl: './rate-application-modal.component.html',
    styleUrls: ['./rate-application-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RateApplicationModalComponent implements AfterViewInit, OnDestroy {
  // Inputs
  close: () => void;
  submit: (score: ApplicationRating, feedbackText?: string) => void;
  openAppStore: () => void;
  submitting$: Observable<boolean> = null;
  submitted$: Observable<boolean> = null;

  @ViewChild('like') like: TemplateRef<any>;
  @ViewChild('rate') rate: TemplateRef<any>;
  @ViewChild('feedback') feedback: TemplateRef<any>;

  readonly maxLength = 512;
  readonly rateAppTemplates = RateAppTemplate;

  form = new FormGroup({
    feedbackText: new FormControl(''),
  });

  activeTemplateId = RateAppTemplate.Like;
  templates: RateAppTemplateData[] = [];
  submitOnClose = true;

  get activeTemplate(): TemplateRef<any> {
    return this.templates.find((templateData) => templateData.id === this.activeTemplateId)?.template;
  }

  constructor(private cd: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.templates = [
      { id: RateAppTemplate.Like, template: this.like },
      { id: RateAppTemplate.Rate, template: this.rate },
      { id: RateAppTemplate.Feedback, template: this.feedback },
    ];
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (!this.submitOnClose) {
      return;
    }

    switch (this.activeTemplateId) {
      case RateAppTemplate.Like: {
        this.submit(ApplicationRating.NoRating);
        break;
      }
      case RateAppTemplate.Feedback: {
        this.submit(ApplicationRating.Dislike);
        break;
      }
    }
  }

  likeTheApp(): void {
    this.submit(ApplicationRating.Like);
    this.submitting$.pipe(awaitSuccessOrCancel(() => this.submitted$)).subscribe(() => {
      this.submitOnClose = false;
      this.goTo(RateAppTemplate.Rate);
    });
  }

  dislikeTheApp(): void {
    this.submit(ApplicationRating.Dislike, this.form.controls['feedbackText'].value);
    this.submitting$.pipe(awaitSuccessOrCancel(() => this.submitted$)).subscribe(() => {
      this.submitOnClose = false;
      this.close();
    });
  }

  goTo(template: RateAppTemplate): void {
    this.activeTemplateId = template;
  }
}
